import { useUserData } from '@/services/user';
import { isPendingConsent } from '@common/atomic/helpers';

export type OnboardingPosition =
    | 'loading'
    | 'error'
    | 'signin'
    | 'unauthenticated'
    | 'signup'
    | 'partner-welcome'
    | 'profile'
    | 'membership'
    | 'complete';

// This list is utilized by `<AuthPageWrapper />` and ensures that the user
// can only access these pages if authenticated.
export const ONBOARDING_POSITIONS: OnboardingPosition[] = [
    'unauthenticated',
    'partner-welcome',
    'profile',
    'membership',
    // These are included in OnboardingPosition above, but NOT included here:
    // 'loading',
    // 'error',
    // 'signin',
    // 'signup',
    // 'complete',
];

export const MOBILE_SPLASH_ONBOARDING_POSITIONS: OnboardingPosition[] = [
    'profile',
    'membership',
];

export const useOnboardingPosition = (): OnboardingPosition => {
    const {
        isLoading,
        error,
        stytchUser,
        userInfo,
        incomingPartnershipInvite,
        membership,
        activePartnershipId,
    } = useUserData();

    if (error) return 'error';
    if (isLoading) return 'loading';
    if (!stytchUser || !userInfo) return 'unauthenticated';

    const atomicStatus = userInfo.atomicStatus;

    // Legacy: anyone who has previously completed Atomic onboarding is
    //  considered to be "complete".
    const isLegacyComplete =
        userInfo.fullAtomicOnboardingFinished ||
        (!!atomicStatus && !isPendingConsent(atomicStatus));

    if (isLegacyComplete) return 'complete';

    // Legacy #2: anyone who already has a membership is considered to be "complete".

    if (membership) return 'complete';

    const partnerWelcomeComplete =
        incomingPartnershipInvite?.isAccepted || activePartnershipId;

    if (incomingPartnershipInvite && !partnerWelcomeComplete) {
        return 'partner-welcome';
    }

    const profileComplete =
        userInfo.firstName && userInfo.onboardingData?.partner?.preferredName;

    if (!profileComplete) {
        return 'profile';
    }

    if (!membership) {
        return 'membership';
    }

    return 'complete';
};
